import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/Header';
import Hero from '../components/Hero';
import CategoryFilter from '../components/CategoryFilter';
import ConferenceCard from '../components/ConferenceCard';
import SubmitConferenceForm from '../components/SubmitConferenceForm';
import Footer from '../components/Footer';
import SEO, { homeJsonLd, conferenceListJsonLd } from '../components/SEO';
import { categories, formats, months } from '../mock';
import { conferenceAPI } from '../services/api';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { ArrowRight, Loader2 } from 'lucide-react';

const Home = () => {
  const [searchParams] = useSearchParams();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedFormat, setSelectedFormat] = useState('All');
  const [selectedMonth, setSelectedMonth] = useState('All Months');
  const [selectedYear, setSelectedYear] = useState('All Years');
  const [filteredConferences, setFilteredConferences] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchConferences();
  }, [searchParams, selectedCategory, selectedFormat, selectedMonth, selectedYear]);

  const fetchConferences = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {};
      
      const searchQuery = searchParams.get('search');
      const locationQuery = searchParams.get('location');
      
      if (searchQuery) params.search = searchQuery;
      if (locationQuery) params.location = locationQuery;
      if (selectedCategory !== 'all') {
        const categoryName = categories.find(c => c.id === selectedCategory)?.name;
        params.category = categoryName;
      }
      if (selectedFormat !== 'All') params.format = selectedFormat;
      if (selectedMonth !== 'All Months') params.month = selectedMonth;
      if (selectedYear !== 'All Years') params.year = selectedYear;

      const response = await conferenceAPI.getAll(params);
      setFilteredConferences(response.data);
    } catch (err) {
      console.error('Error fetching conferences:', err);
      setError('Failed to load conferences. Please try again later.');
      setFilteredConferences([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      <SEO
        title="Discover the World's Best SEO Conferences & Events"
        description="Browse top-rated technical SEO summits, content strategy workshops, and digital marketing networking events. The premier directory for search marketing professionals."
        canonical="https://allseoconferences.com"
        jsonLd={[homeJsonLd(filteredConferences), conferenceListJsonLd(filteredConferences)]}
      />
      <Header />
      <Hero />
      <CategoryFilter
        categories={categories}
        selectedCategory={selectedCategory}
        onSelectCategory={setSelectedCategory}
      />

      <div id="conferences-list" className="container mx-auto px-4 py-16">
        <div className="mb-12">
          <p className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-widest mb-3 font-semibold text-center">
            Curated Calendar
          </p>
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-3 text-center">
            Upcoming Conferences
          </h2>
          <p className="text-gray-600 dark:text-gray-400 text-center mb-8">
            Curated events for the search marketing community.
          </p>
          <div className="flex justify-center">
            <a
              href="/full-calendar"
              className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium transition-colors flex items-center"
            >
              View Full Calendar →
            </a>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-4 mb-8">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Format:
            </span>
            <Select value={selectedFormat} onValueChange={setSelectedFormat}>
              <SelectTrigger className="w-40">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {formats.map((format) => (
                  <SelectItem key={format} value={format}>
                    {format}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Month:
            </span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-48" data-testid="month-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {months.map((month) => (
                  <SelectItem key={month} value={month}>
                    {month}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Year:
            </span>
            <Select value={selectedYear} onValueChange={setSelectedYear}>
              <SelectTrigger className="w-36" data-testid="year-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {['All Years', '2025', '2026', '2027'].map((yr) => (
                  <SelectItem key={yr} value={yr}>
                    {yr}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
          </div>
        ) : error ? (
          <div className="text-center py-16">
            <p className="text-red-500 text-lg">{error}</p>
          </div>
        ) : filteredConferences.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredConferences.map((conference) => (
              <ConferenceCard key={conference.id} conference={conference} />
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <p className="text-gray-500 dark:text-gray-400 text-lg">
              No conferences found matching your criteria.
            </p>
          </div>
        )}
      </div>

      {/* Submit Conference Section */}
      <div className="bg-gray-50 dark:bg-gray-800/50 py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <p className="text-purple-600 dark:text-purple-400 text-xs uppercase tracking-widest mb-3 font-semibold">
                Get Listed
              </p>
              <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-3">
                Submit Your Conference
              </h2>
              <p className="text-gray-600 dark:text-gray-400">
                Share your SEO event with our community. All submissions are reviewed within 24-48 hours.
              </p>
            </div>
            <SubmitConferenceForm />
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Home;